import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { ListRoot, Text, VStack } from '@chakra-ui/react'
import { EDITOR_SHAPE_KEYS, MODAL_TYPES } from '@editor/config/constants'
import { INITIAL_SHAPE_STATE, useEditorContext } from '@editor/contexts/Editor'
import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { hashString, useHashUpdater } from '@editor/hooks/useHashUpdater'
import useInspectionGroup from '@editor/hooks/useInspectionGroup'
import { BlueprintV2 } from '@editor/interfaces/blueprint'
import { DrawingParameterTextAnnotation, DrawingType } from '@editor/interfaces/drawing'
import { EditorPanelItem } from '@editor/interfaces/editor'
import { InspectionItemType } from '@editor/interfaces/inspection'
import { InspectionGroup } from '@editor/interfaces/inspectionGroup'
import { ShapesId } from '@editor/interfaces/shape'
import { updateBlueprint } from '@editor/pages/projects/blueprint-editor/store/blueprintEditor'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { useInspectionItems } from '@editor/pages/projects/hooks/useInspectionItems'
import { useShapes } from '@editor/pages/projects/hooks/useShapes'
import { isDrawingOfType } from '@editor/services/Drawing'
import { COMMON_MESSAGE } from '@editor/services/ErrorHandler'
import { deleteShapes } from '@editor/services/InspectionArea'
import { batchDeleteInspectionGroups, isInspectionGroup } from '@editor/services/InspectionGroup'
import { batchDeleteInspectionItems } from '@editor/services/InspectionSheet'
import { withPromiseContained } from '@editor/services/Util'
import { getVolumeEstimationItems, pollAsyncJobStatus } from '@editor/services/VolumeEstimation'
import { GenericError } from '@editor/services/errors/GenericError'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { useToolUtilSelector } from '@editor/store/selectors'
import {
  setDeletingElementIds,
  setFocusedElementId,
  setHoveredElementId,
  setSelectedElementIds,
  toggleCheckedElementId,
  toggleHiddenElementIds,
  toggleSelectedElementIds,
} from '@editor/store/tool'
import * as Sentry from '@sentry/react'
import { difference, uniq } from 'lodash'
import mixpanel from 'mixpanel-browser'
import { useDebouncedCallback } from 'use-debounce'

import useBlueprint from '../../hooks/useBlueprint'
import { setElementDeleting } from '../../store/editor'
import { Tools } from '../../tools/setup'
import LayerSimple from '../components/LayerSimple'
import { checkIsSelectable, collectItemsByIds, getCurrentAndDescendantIds, getId } from '../utils'

let ignoreHover = false

const useElementsPanel = (
  current?: EditorPanelItem,
  onUpdated?: (item: EditorPanelItem) => void,
  onSelectLayer?: (ids: string[], selected: boolean) => void,
  onCheckToggled?: (ids: string[], checked: boolean) => void,
  onVisibilityToggled?: (ids: string[], invisible: boolean) => void,
  forGroup = false,
) => {
  const {
    defaultCollapsed = false,
    toggleVisibility,
    onDelete,
    onSelect = true,
    isCheckable = false,
    isChecked: isCurrentChecked = false,
    aggregateSelection = false,
    onHover,
    item,
    isSaving = false,
    isDeleting = false,
    isWorking = false,
    children = [],
    errorMessage,
  } = current || {}

  // Hooks
  const { getHashValue, updateHash } = useHashUpdater('it')
  const { fetchInspectionItems } = useInspectionItems()
  const { fetchShapes } = useShapes()
  const { fetchInspectionGroups } = useInspectionGroup()
  const { fetchBlueprints } = useBlueprint()

  // Context
  const { showModal, showErrorModal } = useGlobalModalContext()
  const { getAccessToken } = useContext(UserContext)
  const { selectedTool, changeIsJobRunning, changeSelectedShapeIds } = useEditorContext()

  // Store
  const dispatch = useAppDispatch()
  const inspectionSheet = useAppSelector((state) => state.inspectionItems.inspectionSheet)
  const inspectionItems = useAppSelector((state) => state.inspectionItems.items)
  const shapes = useAppSelector((state) => state.shapes.shapes)
  const hiddenElementIds = useToolUtilSelector((state) => state.hiddenElementIds)
  const selectedElementIds = useToolUtilSelector((state) => state.selectedElementIds)
  const checkedElementIds = useToolUtilSelector((state) => state.checkedElementIds)
  const deletingElementIds = useToolUtilSelector((state) => state.deletingElementIds)
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const inspectionGroups = useAppSelector((state) => state.inspectionGroup.groups)

  // States
  const [collapsed, setCollapsed] = useState(defaultCollapsed)

  // Derived states
  const currentEditorTool = Tools.find((tool) => tool.key === selectedTool)
  const isShapeSelectable = useMemo(
    () => !currentEditorTool || !current || checkIsSelectable(currentEditorTool.config, current, inspectionItems),
    [currentEditorTool, current, inspectionItems],
  )
  const isInvisible = useMemo(() => {
    if (current) {
      if (toggleVisibility && typeof toggleVisibility === 'object' && 'invisible' in toggleVisibility) {
        return toggleVisibility.invisible
      }

      return !difference(getCurrentAndDescendantIds(current, forGroup), hiddenElementIds).length
    }

    return false
  }, [hiddenElementIds, current, toggleVisibility, forGroup])
  const isSelected = useMemo(() => {
    if (!current || !onSelect || !selectedElementIds.length || !isShapeSelectable) return false

    // Nothing to select
    const ids = getCurrentAndDescendantIds(current, forGroup, inspectionItems, currentEditorTool?.config)
    if (!ids.length) return false

    return !difference(ids, selectedElementIds).length
  }, [selectedElementIds, current, inspectionItems, currentEditorTool, isShapeSelectable, onSelect, forGroup])
  const isChecked = useMemo(() => {
    // For aggregate selection, only check immediate children.
    if (aggregateSelection) {
      return (
        children
          ?.filter(
            (child) => (isInspectionGroup(item) && child.isGrouped) || (!isInspectionGroup(item) && !child.isGrouped),
          )
          .every((child) => child.isChecked || checkedElementIds.includes(getId(child))) || false
      )
    }

    return isCurrentChecked || (current && checkedElementIds.includes(getId(current)))
  }, [children, isCurrentChecked, aggregateSelection, current, item, checkedElementIds])
  const isFocused = useMemo(() => {
    if (!current?.inspectionItem) return false
    const hashedId = hashString(current.inspectionItem.inspection_item_id!)
    const currentHash = getHashValue()
    return currentHash === hashedId
  }, [current, getHashValue])

  /**
   * Keep track of all IDs belonging to this item using ref to avoid unnecessary re-renders
   * and to have the latest IDs in event handlers.
   */
  const allIds = useRef<string[]>([])
  useEffect(() => {
    allIds.current = current
      ? getCurrentAndDescendantIds(current, forGroup, inspectionItems, currentEditorTool?.config)
      : []
  }, [current, forGroup, inspectionItems, currentEditorTool])

  /**
   * Enable/disable various features based on the current state
   */

  // Item can be checked through the checkbox
  // can only checked on aggregate if there are children
  const canBeChecked = useMemo(
    () => isCheckable && (!aggregateSelection || (aggregateSelection && children && children?.length > 0)),
    [isCheckable, aggregateSelection, children],
  )

  // Item can be hovered indicating a click behavior is possible.
  // only covers the visual aspects of hover, not the actual hover functionality.
  const isHoverable = useMemo(
    () => (onSelect && isShapeSelectable) || canBeChecked,
    [onSelect, isShapeSelectable, canBeChecked],
  )

  /**
   * Handle visibility toggle.
   * Not to be used for batch operations.
   */
  const handleToggleVisibility = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()

      const ids: string[] = []
      if (current) {
        ids.push(...getCurrentAndDescendantIds(current, forGroup))
      }

      // If a custom function is provided through EditorPanelItem, call it
      if (toggleVisibility) {
        // custom callback only
        if (typeof toggleVisibility === 'function') {
          toggleVisibility(ids, !isInvisible)
          return
        }

        // custom configuration object
        if (typeof toggleVisibility === 'object' && 'onToggle' in toggleVisibility) {
          toggleVisibility.onToggle(ids, !isInvisible)
          return
        }
      }

      // If a custom function is provided through props, call it
      if (onVisibilityToggled) {
        onVisibilityToggled(ids, !isInvisible)
        return
      }

      if (current) {
        if (!isInvisible) {
          ignoreHover = true
        }

        dispatch(setHoveredElementId(''))
        dispatch(toggleHiddenElementIds({ ids, invisible: !isInvisible }))
      }
    },
    [current, isInvisible, toggleVisibility, forGroup, onVisibilityToggled, dispatch],
  )

  const runDeleteShapes = useCallback(
    async (token: string, shapesId: ShapesId) => {
      if (!project || !inspectionArea) return false

      // If the planes we're deleting has been used for volume estimation, make sure to delete both planes.
      const volumeEstimationItems = getVolumeEstimationItems(inspectionItems)
      if (volumeEstimationItems) {
        // Get all volume estimation items that are using the planes we're deleting. Get only plane IDs.
        const polyRemovedVolumeShapeIds = volumeEstimationItems
          .filter((it) => shapesId.polygons.find((id) => it.shape_ids.polygons?.includes(id)))
          .map((it) => it.shape_ids.polygons)
          .flat()

        // Add the planes we're deleting to the list of shapes to be deleted. Make sure to not add duplicates.
        if (polyRemovedVolumeShapeIds) {
          const polyMissingIds = polyRemovedVolumeShapeIds.filter((val) => !shapesId.polygons.includes(val))
          if (polyMissingIds.length) {
            shapesId[EDITOR_SHAPE_KEYS.POLYGONS] = shapesId[EDITOR_SHAPE_KEYS.POLYGONS].concat(polyMissingIds)
          }
        }
      }

      mixpanel.track('Deleting shapes', {
        'Shape amount': shapesId.cylinders.length + shapesId.polygons.length,
        'Shape IDs': shapesId,
      })

      const deleteResult = await deleteShapes(
        token,
        project.project_id,
        inspectionArea.inspection_area_id,
        shapesId,
        showErrorModal,
      )
      if (!deleteResult) return false

      // If the delete operation returns an array, it means it has some pending async job we need to further wait for.
      if (Array.isArray(deleteResult)) {
        await Promise.all(deleteResult.map(async (res) => pollAsyncJobStatus(token, res.job_token)))
      }

      return deleteResult
    },
    [project, inspectionArea, inspectionItems, showErrorModal],
  )

  /**
   * Run delete inspection items to API.
   */
  const runDeleteInspectionItems = useCallback(
    async (token: string, inspectionItemIds: string[]) => {
      if (!inspectionItemIds.length || !project || !inspectionArea || !inspectionSheet) return false

      return batchDeleteInspectionItems(
        token,
        project.project_id,
        inspectionArea.inspection_area_id,
        inspectionSheet.inspection_sheet_id,
        inspectionItemIds,
        showErrorModal,
      )
    },
    [project, inspectionArea, inspectionSheet, showErrorModal],
  )

  /**
   * Run delete inspection groups to API.
   */
  const runDeleteInspectionGroups = useCallback(
    async (token: string, ids: string[]) => {
      if (!ids.length || !project || !inspectionArea) return false

      const toBeDeletedIds = inspectionGroups
        .filter((group) => isInspectionGroup(group.item) && ids.includes(group.item.group_id))
        .map((group) => ({
          group_id: (group.item as InspectionGroup).group_id,
          inspection_area_id: inspectionArea.inspection_area_id,
        }))

      return batchDeleteInspectionGroups(token, project.project_id, toBeDeletedIds)
    },
    [project, inspectionArea, inspectionGroups],
  )

  /**
   * Update drawings to remove any linked annotations to the deleted items.
   *
   * @param token Auth0 access token
   * @param ids The Inspection Item IDs of the deleted items
   */
  const updateDrawings = useCallback(
    async (token: string, ids: string[]) => {
      if (!ids.length || !project || !inspectionArea) return false

      const blueprints = await fetchBlueprints()
      if (!blueprints) return false

      // Find the affected blueprints and remove any linked annotations.
      const affectedBlueprints = blueprints
        .map((bp) => {
          const annotations =
            bp.drawings?.filter(
              (dr) =>
                isDrawingOfType<DrawingParameterTextAnnotation>(dr, DrawingType.TextAnnotation) &&
                dr.drawing_parameters.leadAnchors.some(
                  (anchor) => anchor.inspectionItemId && ids.includes(anchor.inspectionItemId),
                ),
            ) || []

          if (!annotations.length) return null

          // make a copy of the blueprint and remove the annotations
          return {
            ...bp,
            drawings: bp.drawings?.filter((dr) => !annotations.includes(dr)) || [],
          }
        })
        .filter(Boolean) as BlueprintV2[]

      // Update all affected blueprints
      return Promise.all(
        affectedBlueprints.map((bp) =>
          dispatch(
            updateBlueprint({
              accessToken: token,
              projectId: project.project_id,
              areaId: inspectionArea.inspection_area_id,
              blueprint: bp,
            }),
          ).unwrap(),
        ),
      )
    },
    [project, inspectionArea, fetchBlueprints, dispatch],
  )

  /**
   * Handle deletion of saved items.
   *
   * @param ids The IDs to be deleted
   * @param toolPanelItems Generated tool panel items
   */
  const handleDeleteSavedItems = useCallback(
    (ids: string[], toolPanelItems: EditorPanelItem[]) => {
      if (!ids.length || !project || !inspectionArea) return

      // Compile shape IDs to be deleted
      const shapesId: ShapesId = INITIAL_SHAPE_STATE()
      const shapeInspectionItemIds: string[] = []
      ids.forEach((id) => {
        if (shapes.cylinders.some((shape) => shape.shape_id === id)) {
          shapesId.cylinders.push(id)
        }
      })
      ids.forEach((id) => {
        if (shapes.polygons.some((shape) => shape.shape_id === id)) {
          shapesId.polygons.push(id)

          // Also collect inspection item IDs that are using this shape
          shapeInspectionItemIds.push(
            ...inspectionItems
              .filter(
                (it) =>
                  it.shape_ids.polygons?.includes(id) &&
                  !shapeInspectionItemIds.includes(it.inspection_item_id!) &&
                  it.item_type &&
                  ([InspectionItemType.Volume].includes(it.item_type) ||
                    (it.item_type === InspectionItemType.PolygonArea && !it.volume_id)),
              )
              .map((it) => it.inspection_item_id!),
          )
        }
      })
      const selectedShapesCount = shapesId.cylinders.length + shapesId.polygons.length

      // Compile inspection item IDs to be deleted
      const inspectionItemIds = ids.filter((id) => {
        const inspectionItem = inspectionItems.find((it) => it.inspection_item_id === id)
        // Prevent deletion of volume items here, as they should be deleted through shape deletion.
        return inspectionItem && inspectionItem.item_type !== InspectionItemType.Volume
      })

      // Compile inspection group IDs to be deleted
      const inspectionGroupIds = ids.filter((id) =>
        inspectionGroups
          .filter((ig) => isInspectionGroup(ig.item))
          .some((gr) => (gr.item as InspectionGroup).group_id === id),
      )

      if (!selectedShapesCount && !inspectionItemIds.length && !inspectionGroupIds.length) return

      const toBeDeleted = collectItemsByIds(ids, toolPanelItems)
      const allInspectionItemIds = uniq(shapeInspectionItemIds.concat(inspectionItemIds))

      showModal({
        title: `選択した要素を削除してもよろしいですか？`,
        size: 'xl',
        body: (
          <VStack alignItems="start" maxH="600px" overflow="auto">
            <Text>一度削除してしまうと、元に戻せません。</Text>
            <Text>また、関連する測定値が全て削除されます。</Text>

            {/* List the shapes that will be deleted */}
            <ListRoot w="90%" alignSelf="center">
              {toBeDeleted.map((it) => (
                <LayerSimple key={it.key} item={it} />
              ))}
            </ListRoot>
          </VStack>
        ),
        confirmText: '削除',
        modalType: MODAL_TYPES.CONFIRMATION_CRITICAL,
        onConfirm: () => {
          void withPromiseContained(async () => {
            changeIsJobRunning(true)
            dispatch(setDeletingElementIds(ids))
            dispatch(setElementDeleting(true))
            dispatch(setAttentionText({ message: 'データを更新中...' }))

            const token = await getAccessToken()
            if (!token) return

            await Promise.all([
              selectedShapesCount ? runDeleteShapes(token, shapesId) : true,
              inspectionItemIds.length ? runDeleteInspectionItems(token, inspectionItemIds) : true,
              inspectionGroupIds.length ? runDeleteInspectionGroups(token, inspectionGroupIds) : true,
              allInspectionItemIds.length ? updateDrawings(token, allInspectionItemIds) : true,
            ])

            await Promise.all([fetchInspectionItems(), fetchShapes()])

            // intentionally reload later to avoid unintended de-parenting of elements while they reload.
            await fetchInspectionGroups()

            dispatch(setDeletingElementIds([]))
          })
            .catch((error) => {
              showErrorModal(error instanceof GenericError ? error.message : COMMON_MESSAGE)
              Sentry.captureException(error)
            })
            .finally(() => {
              changeIsJobRunning(false)
              dispatch(setAttentionText({ message: '' }))
              dispatch(setElementDeleting(false))
            })
          return true
        },
      })
    },
    [
      project,
      inspectionGroups,
      inspectionItems,
      inspectionArea,
      shapes,
      fetchInspectionGroups,
      fetchInspectionItems,
      fetchShapes,
      changeIsJobRunning,
      runDeleteShapes,
      runDeleteInspectionItems,
      runDeleteInspectionGroups,
      updateDrawings,
      getAccessToken,
      dispatch,
      showModal,
      showErrorModal,
    ],
  )

  /**
   * Handle delete.
   * Can be used for batch operations.
   *
   * @param ids The IDs to be deleted
   * @param toolPanelItems Generated tool panel items
   */
  const handleDelete = useCallback(
    async (ids: string[], toolPanelItems: EditorPanelItem[]) => {
      if (typeof onDelete === 'function') {
        await onDelete()
        return
      }

      handleDeleteSavedItems(ids, toolPanelItems)
    },
    [handleDeleteSavedItems, onDelete],
  )

  /**
   * Handle layer selection.
   *
   * @param dontUnselect If true, it will not unselect the current selection if the current item is already selected.
   */
  const handleSelect = useCallback(
    (dontUnselect = false) => {
      // If if's a shape that is not selectable, do nothing
      // If the items is already selected and dontUnselect is true, do nothing
      if (!isShapeSelectable || (isSelected && dontUnselect)) return

      // If a custom function is provided, use that
      if (onSelectLayer) {
        onSelectLayer(allIds.current, !isSelected)
        return
      }

      if (isSelected) {
        changeSelectedShapeIds(difference(selectedElementIds, allIds.current))
      } else {
        changeSelectedShapeIds(uniq([...selectedElementIds, ...allIds.current]))
      }
    },
    [isSelected, selectedElementIds, isShapeSelectable, onSelectLayer, changeSelectedShapeIds],
  )

  /**
   * Handle hover event.
   * @param flag Mouse hover in or out
   */
  const handleHover = useCallback(
    (flag: boolean) => {
      // If if's a shape that is not selectable, do nothing
      if (!isShapeSelectable) return

      if (typeof onHover === 'function') {
        onHover(flag)
        return
      }

      if (ignoreHover) {
        ignoreHover = false
        return
      }

      if ((!onSelect && !onHover) || !current) return

      dispatch(setHoveredElementId(flag ? getId(current) : ''))
    },
    [isShapeSelectable, onSelect, current, onHover, dispatch],
  )

  /**
   * Handle checking behavior.
   */
  const handleCheck = useCallback(() => {
    if (!current) return

    if (onCheckToggled) {
      const ids = current.isVirtualContainer
        ? getCurrentAndDescendantIds(current, forGroup, inspectionItems)
        : [getId(current)]
      onCheckToggled(ids, !isChecked)
      return
    }

    dispatch(toggleCheckedElementId(getId(current)))
  }, [onCheckToggled, current, isChecked, inspectionItems, forGroup, dispatch])

  const handleDoubleClick = useDebouncedCallback(
    (e: React.MouseEvent) => {
      // No editing while saving, deleting, or failed during new item creation
      if (e.detail !== 2 || isSaving || isDeleting || (errorMessage && isWorking) || !current) return

      // Also check if element is being deleted via deletingElementIds
      const elementId = getId(current)
      const inspectionItemId = current.inspectionItem?.inspection_item_id
      const idsToCheck = inspectionItemId ? [elementId, inspectionItemId] : [elementId]
      if (idsToCheck.some((id) => deletingElementIds.includes(id))) return

      e.preventDefault()
      handleSelect(true)
      if (onUpdated) {
        onUpdated({
          ...current,
          isEditing: true,
        })
      }
    },
    50,
    {
      leading: false,
      trailing: true,
    },
  )

  /**
   * Handle click event
   *
   * @param e Mouse event
   */
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (onSelect && e.detail === 1) handleSelect()
    if (onUpdated) handleDoubleClick(e)
  }

  /**
   * Handle focus event. This is triggered when the user clicks the focus button on an inspection item in the info panel.
   * It toggles focus for the inspection item by updating the URL hash and related selection/focus state,
   * so the canvas can react to the hash change and focus the corresponding item.
   *
   * @param e Mouse event
   */
  const handleFocusItem = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!current?.inspectionItem) return

      // If the current item is already in the URL hash, remove it.
      if (isFocused) {
        updateHash('')
        dispatch(toggleSelectedElementIds({ ids: allIds.current, selected: false }))
        dispatch(setFocusedElementId(''))
        return
      }

      // Update URL hash
      updateHash(current.inspectionItem.inspection_item_id!)
    },
    [current, updateHash, dispatch, isFocused],
  )

  /**
   * Handle hash-based events, such as focusing on specific items or sections when the URL hash changes.
   */
  useEffect(() => {
    if (!current?.inspectionItem) return

    if (isFocused) {
      dispatch(setFocusedElementId(current.inspectionItem.inspection_item_id!))
      dispatch(setSelectedElementIds(allIds.current))
    }
  }, [isFocused, current?.inspectionItem, dispatch])

  return {
    isShapeSelectable,
    collapsed,
    isInvisible,
    isSelected,
    isHoverable,
    isChecked,
    isFocused,
    canBeChecked,
    handleToggleVisibility,
    handleDelete,
    handleSelect,
    handleHover,
    handleClick,
    handleCheck,
    handleFocusItem,
    setCollapsed,
  }
}

export default useElementsPanel
